import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Camera, DatePicker } from "src/components";
import { Avatar } from "src/components/ui/avatar";
import { ValidationType } from "src/enums";
import { Field } from "src/models";
import { ValidationService, _ } from "src/services";
import { ConfigInit, UserSession, UserSessionAction } from "src/store";

export const ProfileInfo: React.FC = () => {

    //#region dependencies

    const dispatch = useDispatch();
    const user: UserSession = useSelector((state: any) => state.UserState);
    const config: ConfigInit = useSelector((state: any) => state.ConfigState);

    //#end region

    //#region state

    const [state, setState] = useState<(Field)[]>([
        {
            order: 0,
            name: "image",
            title: "",
            placeholder: "",
            value: "",
            error: "",
        },
        {
            order: 1,
            name: "fullName",
            title: _("fullName"),
            placeholder: _("fullNamePlaceholder"),
            value: user.user?.fullName || "",
            error: "",
            validations: [ValidationType.Required]
        },
        {
            order: 2,
            name: "gender",
            title: _("gender"),
            placeholder: _("genderPlaceholder"),
            value: user.user?.gender,
            error: "",
            number: true,
            validations: [ValidationType.Required]
        },
        {
            order: 3,
            name: "birthDate",
            title: _("birthday"),
            placeholder: _("birthdayPlaceholder"),
            value: user.user?.birthDate,
            error: "",
            validations: [ValidationType.Required]
        },
    ]);

    const [dragging, setDragging] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    //#end region

    //region methods

    const onSubmit = () => {
        const valid = validate(null, "");
        if (valid) {
            const model: Record<string, any> = {};

            state.forEach((x) => {
                model[x.name] = x.value;
                if (x.number) model[x.name] = +model[x.name];
            });

            dispatch(UserSessionAction.updateProfile(model));
        }
    };

    const validate = (name: string | null, value: any) => {
        const model = ValidationService.validateModelByName(state, name, value);
        setState([...model.fields]);
        return model.valid;
    };

    const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const fullName = e.target.value;
        validate("fullName", fullName);
    };

    const onGenderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        const gender = (value ? +value : undefined) as (1 | 2 | undefined);
        validate("gender", gender);
    };

    const onBirthdayChange = (value?: Date) => {
        validate("birthDate", value);
    };

    const fileReceived = (files: FileList | null) => {
        if (files && files.length) {
            const file = files[0];

            if (!file) return;

            if (!file.type.toLowerCase().match(/.(jpg|jpeg|png|bmp)$/i)) {
                toast.error(_("profilePicFormatError"));
                return;
            }


            if (file.size / 1024 > 5000) {
                toast.error(_("profilePicSizeError"));
                return;
            }

            const reader = new FileReader();

            reader.onload = ((e: ProgressEvent<FileReader>) => {
                if (!e.target) return;
                const source = e.target.result?.toString() || "";
                const image = new Image();
                image.onload = () => {

                    try {
                        const canvas = document.createElement('canvas'),
                            max_size = 300;
                        let width = image.width,
                            height = image.height;

                        if (width > height) {
                            if (width > max_size) {
                                height *= max_size / width;
                                width = max_size;
                            }
                        } else {
                            if (height > max_size) {
                                width *= max_size / height;
                                height = max_size;
                            }
                        }

                        canvas.width = width;
                        canvas.height = height;
                        canvas.getContext('2d')?.drawImage(image, 0, 0, width, height);
                        const dataUrl = canvas.toDataURL('image/jpeg');
                        validate("image", dataUrl);
                    }
                    catch {
                        validate("image", source);
                    }
                };
                image.src = source;
            });

            reader.readAsDataURL(file);
        }
    };

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        e.preventDefault();
        const files = e.target.files;
        fileReceived(files);
    };

    const uploaderClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (fileRef && fileRef.current) fileRef.current.click();
    };


    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(true);
    };
    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
    };
    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(true);
    };
    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);

        const files = e.dataTransfer.files;
        fileReceived(files);
        e.dataTransfer.clearData();
    };

    //#end region

    //#region effects

    useEffect(() => {

        setState([
            {
                order: 0,
                name: "image",
                title: "",
                placeholder: "",
                value: "",
                error: "",
                unauth: true,
            },
            {
                order: 1,
                name: "fullName",
                title: _("fullName"),
                placeholder: _("fullNamePlaceholder"),
                value: user.user?.fullName || "",
                error: "",
                unauth: true,
                validations: [ValidationType.Required]
            },
            {
                order: 2,
                name: "gender",
                title: _("gender"),
                placeholder: _("genderPlaceholder"),
                value: user.user?.gender,
                error: "",
                unauth: true,
                validations: [ValidationType.Required]
            },
            {
                order: 3,
                name: "birthDate",
                title: _("birthday"),
                placeholder: _("birthdayPlaceholder"),
                value: user.user?.birthDate,
                error: "",
                unauth: true,
                validations: [ValidationType.Required]
            }
        ]);

    }, [user.user]);

    //#end region

    const image = state.find((x) => x.name === "image");
    const fullName = state.find((x) => x.name === "fullName");
    const gender = state.find((x) => x.name === "gender");
    const birthDate = state.find((x) => x.name === "birthDate");

    return (
        <div className="no-package d-flex flex-column justify-content-center align-items-center py-4 px-4">
            <div className="page-profile--avatar-area text-center mx-auto mb-4">
                <div className={`upload-box${dragging ? " dragging" : ""}`}
                    onClick={uploaderClick}
                    onDragEnd={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                >
                    <div className="hold">
                        <span className="btn-file-edit d-block">
                            <div className="icon-edit-container">
                                <span className="camera-icon">
                                    <Camera />
                                </span>
                            </div>
                            {!image?.value && <Avatar />}
                            {
                                image?.value &&
                                <div style={{ width: "100%", height: "100%", position: "relative" }}>
                                    <img alt="" src={image.value} className="profile-img contain" />
                                </div>
                            }
                            <input type="file" accept="image/*" ref={fileRef} onChange={onFileChange} />
                        </span>
                    </div>
                </div>
            </div>
            <div className="w-100 my-4">
                <div className="row">
                    <div className="col-12 col-md-6">
                        <div className="form-group">
                            <label>{_("fullName")}</label>
                            <input
                                value={fullName?.value}
                                onChange={onNameChange}
                                placeholder={_("fullNamePlaceholder")}
                                className={`form-control-transparent page-profile--full-name form-control text-rtl text-right focusable${fullName?.error ? " is-invalid" : ""}`}
                                maxLength={config.config.appSetting.fullNameMaxLength}
                                type="text"
                            />
                            {fullName?.error && <div className="invalid-feedback text-rtl">{fullName.error}</div>}
                        </div>
                    </div>
                    <div className="col-12 col-md-6">
                        <div className="form-group">
                            <label>{_("gender")}</label>
                            <select
                                required
                                value={gender?.value || ""}
                                onChange={onGenderChange}
                                placeholder={_("genderPlaceholder")}
                                className={`form-control-transparent page-profile--full-name form-control text-rtl text-right focusable${gender?.error ? " is-invalid" : ""}`}
                            >
                                <option value="">{_("genderPlaceholder")}</option>
                                <option value={1}>{_("male")}</option>
                                <option value={2}>{_("female")}</option>
                            </select>
                            {gender?.error && <div className="invalid-feedback text-rtl">{gender.error}</div>}
                        </div>
                    </div>
                    <div className="col-12 col-md-6">
                        <div className="form-group">
                            <label>{_("birthday")}</label>
                            <DatePicker
                                value={birthDate?.value}
                                onChange={onBirthdayChange}
                                placeholder={_("birthdayPlaceholder")}
                                className={`form-control-transparent page-profile--full-name form-control text-rtl text-right focusable${birthDate?.error ? " is-invalid" : ""}`}
                            />
                            {birthDate?.error && <div className="invalid-feedback text-rtl d-block">{birthDate.error}</div>}
                        </div>
                    </div>
                </div>
            </div>
            <div className="w-100 my-4 ltr">
                <div className="col-12 col-md-4 offset-md-4">
                    <button onClick={onSubmit}
                        className={`focusable btn btn-primary btn-md btn-submit py-1 focusable text-rtl w-100${user.loading ? " btn-loading" : ""}`}>
                        {_("save")}
                    </button>
                </div>
            </div>
        </div>
    );
};